import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, FileText, Route, Video, MessageSquare } from 'lucide-react';
import type { PlayerActivityEvent } from '@/hooks/usePlayerActivity';

type ActivitySortKey = 'created' | 'player' | 'type' | 'league';
type ActivitySortDirection = 'asc' | 'desc';
type ActivitySort = { key: ActivitySortKey; direction: ActivitySortDirection };

const SORT_OPTIONS: Array<{ key: ActivitySortKey; label: string }> = [
  { key: 'created', label: 'Added' },
  { key: 'player', label: 'Player' },
  { key: 'type', label: 'Type' },
  { key: 'league', label: 'League' },
];

const TYPE_META = {
  note: { label: 'Note', icon: MessageSquare },
  viewing: { label: 'Viewing', icon: CalendarDays },
  video: { label: 'Video', icon: Video },
  schedule: { label: 'Schedule', icon: Route },
} satisfies Record<PlayerActivityEvent['type'], { label: string; icon: typeof FileText }>;

export function ClientActivityWall({
  items,
  empty,
  compact = false,
}: {
  items: PlayerActivityEvent[];
  empty: string;
  compact?: boolean;
}) {
  const [sort, setSort] = useState<ActivitySort>({ key: 'created', direction: 'desc' });
  const groups = useMemo(() => groupByCreatedDate(sortActivity(items, sort), sort), [items, sort]);

  function updateSort(key: ActivitySortKey) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  }

  if (items.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-border bg-white px-4 py-6 text-sm text-muted">
        {empty}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-border bg-white px-3 py-2">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
          Sort agent activity
        </span>
        <div className="flex max-w-full gap-1 overflow-x-auto">
          {SORT_OPTIONS.map((option) => {
            const active = sort.key === option.key;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => updateSort(option.key)}
                className={[
                  'focus-ring inline-flex h-8 shrink-0 items-center gap-1 rounded-full border px-2.5 font-mono text-[11px] font-semibold',
                  active ? 'border-bartlett bg-bartlett text-white' : 'border-border bg-bg text-muted hover:border-bartlett/40',
                ].join(' ')}
              >
                <span>{option.label}</span>
                {active && <span className="text-[9px] uppercase opacity-80">{sort.direction}</span>}
              </button>
            );
          })}
        </div>
      </div>
      {groups.map((group) => (
        <section key={group.date} className="rounded-control border border-border bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-border bg-bartlettIce/60 px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <CalendarDays size={14} className="shrink-0 text-muted" />
              <h3 className="truncate font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-ink">
                {formatDate(group.date)}
              </h3>
            </div>
            <span className="shrink-0 font-mono text-[11px] text-muted">{group.items.length} additions</span>
          </div>
          <div className={compact ? 'divide-y divide-border/70' : 'grid divide-y divide-border/70'}>
            {group.items.map((item) => (
              <ActivityRow key={item.id} item={item} compact={compact} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ActivityRow({ item, compact }: { item: PlayerActivityEvent; compact: boolean }) {
  const meta = TYPE_META[item.type];
  const Icon = meta.icon;
  const playerName = item.displayName ?? `${item.firstName} ${item.lastName}`;
  return (
    <Link
      to={`/people/${item.playerId}?tab=${item.type === 'note' ? 'timeline' : item.type === 'viewing' ? 'viewings' : item.type}`}
      className="grid min-w-0 gap-2 px-3 py-3 text-sm transition-colors hover:bg-bartlettIce/45 sm:grid-cols-[minmax(180px,0.8fr)_minmax(220px,1fr)_auto]"
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-semibold text-ink">{playerName}</span>
          <span className="shrink-0 rounded-full border border-border bg-bg px-1.5 py-0.5 font-mono text-[10px] text-muted">
            {item.currentLeague ?? 'file'}
          </span>
        </div>
        <div className="mt-0.5 truncate font-mono text-[11px] text-muted">
          {[item.currentTeam, addedTime(item.createdAt)].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-bartlettGold/40 bg-bartlettGold/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-bartlett">
            <Icon size={11} />
            {meta.label}
          </span>
          <span className="truncate font-semibold text-ink">{item.title}</span>
        </div>
        {item.body && (
          <p
            className={[
              'mt-1 whitespace-pre-wrap break-words text-[13px] leading-5 text-muted',
              compact ? 'max-w-3xl' : 'max-w-4xl',
            ].join(' ')}
          >
            {item.body}
          </p>
        )}
      </div>
      <div className="hidden items-center justify-end text-muted sm:flex">
        <ArrowRight size={14} />
      </div>
    </Link>
  );
}

function groupByCreatedDate(items: PlayerActivityEvent[], sort: ActivitySort): Array<{ date: string; items: PlayerActivityEvent[] }> {
  const groups = new Map<string, PlayerActivityEvent[]>();
  for (const item of items) {
    const date = new Date(item.createdAt * 1000).toISOString().slice(0, 10);
    groups.set(date, [...(groups.get(date) ?? []), item]);
  }
  return [...groups.entries()]
    .map(([date, groupItems]) => ({ date, items: groupItems }))
    .sort((a, b) => (sort.direction === 'asc' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)));
}

function sortActivity(items: PlayerActivityEvent[], sort: ActivitySort): PlayerActivityEvent[] {
  return [...items].sort((a, b) => {
    const result = compareValues(sortValue(a, sort.key), sortValue(b, sort.key));
    if (result !== 0) return sort.direction === 'asc' ? result : -result;
    return b.createdAt - a.createdAt || playerName(a).localeCompare(playerName(b));
  });
}

function sortValue(item: PlayerActivityEvent, key: ActivitySortKey): string | number | null {
  switch (key) {
    case 'created':
      return item.createdAt;
    case 'player':
      return playerName(item);
    case 'type':
      return item.type;
    case 'league':
      return item.currentLeague;
  }
}

function compareValues(a: string | number | null, b: string | number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

function playerName(item: PlayerActivityEvent): string {
  return item.displayName ?? `${item.firstName} ${item.lastName}`;
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function addedTime(value: number): string {
  return new Date(value * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
